import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import { fetchPosts, type BeehiivPost } from "@/lib/beehiiv";

const ROW_SIZE = 10;
const ALL_POSTS_SIZE = 30;

type SectionKey = "newest" | "popular" | "hiddenGems" | "all";

function formatDate(timestamp: number) {
  return new Date(timestamp * 1000).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function PostCard({ post }: { post: BeehiivPost }) {
  return (
    <Link
      to={`/newsletter/${post.slug}`}
      className="group flex flex-col shrink-0 w-[280px] sm:w-[320px] rounded-xl bg-slate-card border border-white/10 overflow-hidden hover:border-lime/30 transition-colors"
    >
      {post.thumbnail_url && (
        <div className="aspect-video w-full overflow-hidden bg-white/5">
          <img
            src={post.thumbnail_url}
            alt=""
            className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
        </div>
      )}
      <div className="p-4 flex flex-col flex-1 min-w-0">
        <h2 className="font-heading text-lg font-bold text-white group-hover:text-lime transition-colors line-clamp-2">
          {post.title}
        </h2>
        {post.subtitle && (
          <p className="text-secondary text-sm mt-1 line-clamp-2">{post.subtitle}</p>
        )}
        <div className="flex items-center gap-2 mt-3 text-secondary text-xs">
          {post.authors?.length > 0 && <span>{post.authors.join(", ")}</span>}
          {post.authors?.length > 0 && <span>·</span>}
          <time dateTime={new Date(post.publish_date * 1000).toISOString()}>
            {formatDate(post.publish_date)}
          </time>
        </div>
      </div>
    </Link>
  );
}

function HorizontalRow({
  title,
  posts,
  loading,
}: {
  title: string;
  posts: BeehiivPost[];
  loading: boolean;
}) {
  return (
    <section className="animate-[fade-in_0.4s_ease-out_both]">
      <h2 className="font-heading text-xl font-bold text-white mb-4 px-1">
        {title}
      </h2>
      <div className="overflow-x-auto overflow-y-hidden pb-2 -mx-4 px-4 nl-horizontal-scroll">
        <div className="flex gap-4 w-max">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="shrink-0 w-[280px] sm:w-[320px] rounded-xl bg-slate-card border border-white/10 overflow-hidden animate-pulse"
              >
                <div className="aspect-video bg-white/5" />
                <div className="p-4 space-y-2">
                  <div className="h-5 bg-white/10 rounded w-4/5" />
                  <div className="h-4 bg-white/5 rounded w-full" />
                  <div className="h-3 bg-white/5 rounded w-1/3 mt-2" />
                </div>
              </div>
            ))
          ) : posts.length === 0 ? (
            <p className="text-secondary text-sm py-8">No posts in this section.</p>
          ) : (
            posts.map((post) => <PostCard key={post.id} post={post} />)
          )}
        </div>
      </div>
    </section>
  );
}

export default function NewsletterPage() {
  const [sections, setSections] = useState<Record<SectionKey, BeehiivPost[]>>({
    newest: [],
    popular: [],
    hiddenGems: [],
    all: [],
  });
  const [loading, setLoading] = useState<Record<SectionKey, boolean>>({
    newest: true,
    popular: true,
    hiddenGems: true,
    all: true,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    function done(key: SectionKey, data: BeehiivPost[] | null) {
      if (cancelled) return;
      setSections((prev) => ({ ...prev, [key]: data ?? [] }));
      setLoading((prev) => ({ ...prev, [key]: false }));
    }

    // Newest: latest by publish_date
    fetchPosts({
      limit: ROW_SIZE,
      page: 1,
      order_by: "publish_date",
      direction: "desc",
    }).then((res) => done("newest", res?.data ?? null));

    // Popular: same as newest (Beehiiv doesn’t expose engagement sort; use tag "popular" in Beehiiv if you have it)
    fetchPosts({
      limit: ROW_SIZE,
      page: 1,
      order_by: "publish_date",
      direction: "desc",
    }).then((res) => done("popular", res?.data ?? null));

    // Hidden gems: try tag "hidden-gems" first, then fallback to oldest posts
    fetchPosts({
      limit: ROW_SIZE,
      page: 1,
      order_by: "publish_date",
      direction: "asc",
      content_tags: ["hidden-gems"],
    }).then((res) => {
      if (cancelled) return;
      if (res?.data && res.data.length > 0) {
        done("hiddenGems", res.data);
      } else {
        fetchPosts({
          limit: ROW_SIZE,
          page: 1,
          order_by: "publish_date",
          direction: "asc",
        }).then((r) => done("hiddenGems", r?.data ?? null));
      }
    }).catch(() => done("hiddenGems", null));

    // All posts: larger set
    fetchPosts({
      limit: ALL_POSTS_SIZE,
      page: 1,
      order_by: "publish_date",
      direction: "desc",
    }).then((res) => {
      if (cancelled) return;
      if (!res && sections.all.length === 0) setError("Newsletter is not configured or failed to load.");
      done("all", res?.data ?? null);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Navbar />
      <main className="min-h-screen max-w-6xl mx-auto px-4 py-8 sm:py-16">
        <header className="text-center mb-12 animate-[fade-in_0.4s_ease-out]">
          <span className="inline-block bg-white/5 border border-white/10 text-lime text-sm font-medium rounded-full px-4 py-1.5">
            Newsletter
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-4">
            AI is evolving. So should your inbox.
          </h1>
          <p className="text-secondary mt-2 max-w-md mx-auto">
            Stay ahead of the curve with weekly updates on everything AI, from
            breakthroughs in research to tools changing the game.
          </p>
        </header>

        {error && (
          <div className="rounded-xl bg-white/5 border border-white/10 p-6 text-center text-secondary mb-8">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-12">
          <HorizontalRow
            title="Newest"
            posts={sections.newest}
            loading={loading.newest}
          />
          <HorizontalRow
            title="Hidden gems"
            posts={sections.hiddenGems}
            loading={loading.hiddenGems}
          />
          <HorizontalRow
            title="All posts"
            posts={sections.all}
            loading={loading.all}
          />
        </div>

        <div className="mt-16 pt-8 border-t border-white/10">
          <Footer />
        </div>
      </main>
    </>
  );
}
